import { Text } from 'app/design/primitives/text';
import type { AuthenticationMethod } from 'app/lib/authentication-method';
import { useTheme } from 'app/shopify';
import type { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';

interface AuthenticationMethodOptionProps extends PropsWithChildren {
  flex?: boolean;
  lastUsedMethod?: AuthenticationMethod;
  method: AuthenticationMethod;
}

interface LastUsedAuthenticationMethodBadgeProps {
  floating?: boolean;
  method: AuthenticationMethod;
}

export function LastUsedAuthenticationMethodBadge({
  floating = false,
  method,
}: LastUsedAuthenticationMethodBadgeProps) {
  const { t } = useTranslation('login');
  const theme = useTheme();

  return (
    <View
      accessible
      accessibilityLabel={`${t('lastUsedMethod')}: ${method}`}
      pointerEvents="none"
      style={[
        styles.badge,
        {
          backgroundColor: theme.colors['surface.brand.default'],
          borderColor: theme.colors['surface.primary'],
          borderRadius: theme.borderRadii['radius.lg'],
          borderWidth: theme.borderWidths['border-width.emphasized'],
          paddingHorizontal: theme.spacing['spacing.general.x2'],
        },
        floating && [styles.floatingBadge, { right: theme.spacing['spacing.general.x2'] }],
      ]}
      testID={`last-used-authentication-method-${method.toLowerCase()}`}
    >
      <Text color="white" fontSize={11} fontWeight="700">
        {t('lastUsedMethod')}
      </Text>
    </View>
  );
}

export function AuthenticationMethodOption({
  children,
  flex = false,
  lastUsedMethod,
  method,
}: AuthenticationMethodOptionProps) {
  return (
    <View style={[styles.container, flex && styles.flex]}>
      {children}
      {lastUsedMethod === method ? <LastUsedAuthenticationMethodBadge floating method={method} /> : null}
    </View>
  );
}

const styles = StyleSheet.create({
  badge: {
    paddingVertical: 2,
  },
  floatingBadge: {
    position: 'absolute',
    top: -10,
    zIndex: 1,
  },
  container: {
    position: 'relative',
  },
  flex: {
    flex: 1,
  },
});
